import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import apiClient from '@/services/client';
import { endpoints } from '@/services/endpoints';

interface TrainingModalContextValue {
  isOpen: boolean;
  trainingSlug: string | null;
  openModal: (slug: string) => void;
  closeModal: (shouldNavigateToTrainings?: boolean) => void;
}

const TrainingModalContext = createContext<TrainingModalContextValue | null>(null);

interface TrainingModalProviderProps {
  children: React.ReactNode;
}

export function TrainingModalProvider({ children }: TrainingModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [trainingSlug, setTrainingSlug] = useState<string | null>(null);

  const openedViaHashRef = useRef(false);
  const previousBodyOverflowRef = useRef<string | null>(null);
  const previousScrollYRef = useRef<number | null>(null);
  const previousBodyPositionRef = useRef<string | null>(null);
  const previousBodyTopRef = useRef<string | null>(null);

  const openModal = useCallback((slug: string) => {
    setTrainingSlug(slug);
    setIsOpen(true);

    if (previousBodyOverflowRef.current === null) {
      previousBodyOverflowRef.current = document.body.style.overflow ?? '';
      previousScrollYRef.current = window.scrollY;
      previousBodyPositionRef.current = document.body.style.position ?? '';
      previousBodyTopRef.current = document.body.style.top ?? '';

      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    if (!window.location.hash?.startsWith('#training-')) {
      openedViaHashRef.current = false;
    }
  }, []);

  const closeModal = useCallback((shouldNavigateToTrainings = false) => {
    setIsOpen(false);
    setTrainingSlug(null);

    if (previousBodyOverflowRef.current !== null) {
      document.body.style.overflow = previousBodyOverflowRef.current;
      document.body.style.position = previousBodyPositionRef.current;
      document.body.style.top = previousBodyTopRef.current;
      document.body.style.width = '';

      if (previousScrollYRef.current !== null) {
        window.scrollTo(0, previousScrollYRef.current);
      }

      previousBodyOverflowRef.current = null;
      previousScrollYRef.current = null;
      previousBodyPositionRef.current = null;
      previousBodyTopRef.current = null;
    }

    if (openedViaHashRef.current || shouldNavigateToTrainings) {
      openedViaHashRef.current = false;
      window.dispatchEvent(new CustomEvent('training-modal-close-navigate', {
        detail: { path: '/tutoriais/treinamentos' },
      }));
    } else {
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }
  }, []);

  useEffect(() => {
    const checkHashAndOpenModal = async () => {
      const hash = window.location.hash;
      if (hash?.startsWith('#training-')) {
        const hashValue = hash.substring(10);

        try {
          const response = await apiClient.get(endpoints.trainings.byHash(hashValue));
          const training = response.data?.data;

          if (training?.slug) {
            openedViaHashRef.current = true;
            openModal(training.slug);
          }
        } catch (error) {
          console.error('Erro ao buscar treinamento por hash:', error);
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } else {
        openedViaHashRef.current = false;
      }
    };

    checkHashAndOpenModal();

    const handleHashChange = () => {
      checkHashAndOpenModal();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [openModal]);

  const value: TrainingModalContextValue = {
    isOpen,
    trainingSlug,
    openModal,
    closeModal,
  };

  return (
    <TrainingModalContext.Provider value={value}>
      {children}
    </TrainingModalContext.Provider>
  );
}

export function useTrainingModal(): TrainingModalContextValue {
  const context = useContext(TrainingModalContext);
  if (!context) {
    throw new Error('useTrainingModal deve ser usado dentro de um TrainingModalProvider');
  }
  return context;
}
